import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchLaptopById,
  fetchLaptopHistory,
  archiveLaptopRpc,
  unarchiveLaptopRpc,
} from '@/services/laptops';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Laptop } from '@/types/models';
import { Modal } from '@/components/common/Modal';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { LaptopForm } from './LaptopForm';
import { RemoveLaptopModal } from './RemoveLaptopModal';
import { ReconfigureHardwareModal } from './ReconfigureHardwareModal';
import { ArchiveRecordModal } from '@/components/common/ArchiveRecordModal';
import { useAuth } from '@/context/AuthContext';
import {
  Laptop as LaptopIcon,
  Cpu,
  HardDrive,
  Battery,
  Edit2,
  Trash2,
  Building2,
  Clock,
  QrCode,
  Loader2,
  Archive,
  RotateCcw,
  Wrench,
} from 'lucide-react';

interface LaptopDetailProps {
  laptopId: string;
  onClose: () => void;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-PK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatCurrency(val: number | null | undefined): string {
  if (val == null) return '—';
  return `Rs. ${val.toLocaleString('en-PK')}`;
}

export const LaptopDetail: React.FC<LaptopDetailProps> = ({ laptopId, onClose }) => {
  const { hasPermission, role } = useAuth();
  const isOwner = role === 'owner';
  const canViewCosts = hasPermission('purchases');
  const queryClient = useQueryClient();
  const [showEditForm, setShowEditForm] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showReconfigModal, setShowReconfigModal] = useState(false);
  const [unarchiveLoading, setUnarchiveLoading] = useState(false);
  const [unarchiveError, setUnarchiveError] = useState<string | null>(null);

  const { data: laptop, isLoading, error, refetch } = useQuery<Laptop>({
    queryKey: queryKeys.laptops.detail(laptopId),
    queryFn: () => fetchLaptopById(laptopId),
  });

  const { data: history = [] } = useQuery({
    queryKey: queryKeys.laptops.history(laptopId),
    queryFn: () => fetchLaptopHistory(laptopId),
    enabled: !!laptop,
  });

  const handleUnarchive = async () => {
    if (!laptop) return;
    try {
      setUnarchiveLoading(true);
      setUnarchiveError(null);
      await unarchiveLaptopRpc(laptop.id);
      void queryClient.invalidateQueries({ queryKey: queryKeys.laptops.all });
      void refetch();
    } catch (err: unknown) {
      setUnarchiveError(parseDatabaseError(err));
    } finally {
      setUnarchiveLoading(false);
    }
  };

  if (showEditForm && laptop) {
    return (
      <Modal isOpen title={`Edit Laptop — ${laptop.stock_id}`} onClose={() => setShowEditForm(false)}>
        <LaptopForm
          laptop={laptop}
          onSuccess={() => {
            setShowEditForm(false);
            void queryClient.invalidateQueries({ queryKey: queryKeys.laptops.all });
            void refetch();
          }}
          onCancel={() => setShowEditForm(false)}
        />
      </Modal>
    );
  }

  const isArchived = !!laptop?.archived_at;
  const canRemoveFromStock =
    canViewCosts &&
    laptop &&
    !isArchived &&
    !['sold', 'scrapped'].includes(laptop.status);

  return (
    <Modal isOpen title={`Laptop Details — ${laptop?.stock_id || '...'}`} onClose={onClose}>
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
        </div>
      )}

      {error && <ErrorAlert message={parseDatabaseError(error)} onRetry={() => refetch()} />}
      {unarchiveError && <ErrorAlert message={unarchiveError} />}

      {laptop && !isLoading && (
        <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
          {/* Archived Banner */}
          {isArchived && (
            <div className="p-3 bg-slate-100 border border-slate-300 rounded-lg flex items-start justify-between gap-2 text-xs text-slate-800">
              <div className="flex items-start space-x-2">
                <Archive className="w-4 h-4 text-slate-600 shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold uppercase tracking-wider block">Archived Record (Hidden from Active Operations)</span>
                  <p className="text-[11px] text-slate-600 mt-0.5">
                    Reason: <em>{laptop.archive_reason || 'No explanation provided'}</em> • Archived on {formatDate(laptop.archived_at!)}
                  </p>
                </div>
              </div>
              {isOwner && (
                <button
                  type="button"
                  onClick={handleUnarchive}
                  disabled={unarchiveLoading}
                  className="inline-flex items-center space-x-1 px-2.5 py-1 bg-white border border-slate-300 text-slate-700 text-xs font-semibold rounded hover:bg-slate-50 transition-colors shrink-0"
                >
                  {unarchiveLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                  <span>Restore</span>
                </button>
              )}
            </div>
          )}

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 pb-3 border-b border-brand-border">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-lg bg-brand-green/10 flex items-center justify-center text-brand-green shrink-0">
                <LaptopIcon className="w-6 h-6" />
              </div>
              <div>
                <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                  <span className="px-2 py-0.5 bg-brand-black text-white text-xs font-bold rounded">
                    {laptop.stock_id}
                  </span>
                  {isArchived && (
                    <span className="px-2 py-0.5 bg-slate-200 text-slate-800 text-xs font-bold rounded-full uppercase">
                      Archived
                    </span>
                  )}
                  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                    laptop.status === 'in_stock' ? 'bg-green-100 text-green-800' :
                    laptop.status === 'sold' ? 'bg-gray-100 text-gray-800' :
                    laptop.status === 'under_repair' ? 'bg-amber-100 text-amber-800' :
                    laptop.status === 'reserved' ? 'bg-blue-100 text-blue-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {laptop.status}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-brand-black mt-1">
                  {laptop.brand} {laptop.model}
                </h3>
              </div>
            </div>

            <div className="flex items-center space-x-2 flex-wrap gap-y-1.5">
              <button
                type="button"
                onClick={() => setShowQrModal(true)}
                className="inline-flex items-center space-x-1 px-3 py-1.5 bg-brand-bg text-brand-black text-xs font-semibold rounded-md border border-brand-border hover:bg-white transition-colors"
              >
                <QrCode className="w-3.5 h-3.5" />
                <span>QR Label</span>
              </button>
              <button
                type="button"
                onClick={() => setShowEditForm(true)}
                className="inline-flex items-center space-x-1 px-3 py-1.5 bg-brand-green text-white text-xs font-semibold rounded-md hover:bg-brand-green-hover transition-colors shadow-sm"
              >
                <Edit2 className="w-3.5 h-3.5" />
                <span>Edit</span>
              </button>
              {canRemoveFromStock && (
                <button
                  type="button"
                  onClick={() => setShowRemoveModal(true)}
                  className="inline-flex items-center space-x-1 px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 text-xs font-semibold rounded-md hover:bg-red-100 transition-colors"
                  title="Remove laptop from active stock (mark as scrapped)"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Remove from Stock</span>
                </button>
              )}
              {isOwner && !isArchived && laptop.status === 'in_stock' && (
                <button
                  type="button"
                  onClick={() => setShowReconfigModal(true)}
                  className="inline-flex items-center space-x-1 px-3 py-1.5 bg-purple-50 text-purple-800 border border-purple-300 text-xs font-semibold rounded-md hover:bg-purple-100 transition-colors"
                  title="Reconfigure RAM/Storage hardware components for this in-stock laptop"
                >
                  <Wrench className="w-3.5 h-3.5 text-purple-600" />
                  <span>Reconfigure Hardware</span>
                </button>
              )}
              {isOwner && !isArchived && (
                <button
                  type="button"
                  onClick={() => setShowArchiveModal(true)}
                  className="inline-flex items-center space-x-1 px-3 py-1.5 bg-slate-100 text-slate-700 border border-slate-300 text-xs font-semibold rounded-md hover:bg-slate-200 transition-colors"
                  title="Archive / Hide this test or legacy laptop record"
                >
                  <Archive className="w-3.5 h-3.5" />
                  <span>Archive Record</span>
                </button>
              )}
            </div>
          </div>

          {showReconfigModal && laptop && (
            <ReconfigureHardwareModal
              laptop={laptop}
              isOpen={showReconfigModal}
              onClose={() => setShowReconfigModal(false)}
              onSuccess={() => {
                setShowReconfigModal(false);
                void queryClient.invalidateQueries({ queryKey: queryKeys.laptops.all });
                void refetch();
              }}
            />
          )}

          {showArchiveModal && (
            <ArchiveRecordModal
              isOpen={showArchiveModal}
              onClose={() => setShowArchiveModal(false)}
              entityType="Laptop"
              identifier={`${laptop.stock_id} • ${laptop.brand} ${laptop.model}`}
              subIdentifier={laptop.serial_number ? `S/N: ${laptop.serial_number}` : undefined}
              warnings={[
                ...(laptop.status === 'reserved' ? ['Laptop is currently reserved for a sale. Archiving will be blocked.'] : []),
                ...(laptop.status === 'under_repair' ? ['Laptop is currently under repair. Archiving will be blocked.'] : []),
              ]}
              onConfirm={async (reason) => {
                await archiveLaptopRpc({ laptopId: laptop.id, reason });
                setShowArchiveModal(false);
                void queryClient.invalidateQueries({ queryKey: queryKeys.laptops.all });
                void refetch();
              }}
            />
          )}

          {showRemoveModal && (
            <RemoveLaptopModal
              laptop={laptop}
              isOpen={showRemoveModal}
              onClose={() => setShowRemoveModal(false)}
              onSuccess={() => {
                setShowRemoveModal(false);
                void refetch();
              }}
            />
          )}

          {/* Pricing Box */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-brand-bg p-3 rounded-lg border border-brand-border">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-muted">Selling Price</p>
              <p className="text-base font-bold text-brand-green">{formatCurrency(laptop.asking_price)}</p>
            </div>
            {laptop.minimum_price && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-muted">Minimum Price</p>
                <p className="text-sm font-semibold text-brand-black">{formatCurrency(laptop.minimum_price)}</p>
              </div>
            )}
            {canViewCosts && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-muted">Purchase Cost</p>
                <p className="text-sm font-semibold text-brand-black">{formatCurrency(laptop.purchase_price)}</p>
              </div>
            )}
          </div>

          {/* Specifications Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
            <div className="p-2.5 bg-white rounded border border-brand-border">
              <p className="font-semibold text-brand-muted">Serial Number</p>
              <p className="font-medium text-brand-black">{laptop.serial_number || '—'}</p>
            </div>
            <div className="p-2.5 bg-white rounded border border-brand-border">
              <p className="font-semibold text-brand-muted">Service Tag</p>
              <p className="font-medium text-brand-black">{laptop.service_tag || '—'}</p>
            </div>
            <div className="p-2.5 bg-white rounded border border-brand-border">
              <p className="font-semibold text-brand-muted">Condition</p>
              <p className="font-medium text-brand-black">{laptop.condition}</p>
            </div>
            <div className="p-2.5 bg-white rounded border border-brand-border">
              <p className="font-semibold text-brand-muted flex items-center space-x-1">
                <Cpu className="w-3.5 h-3.5 text-brand-green" />
                <span>CPU</span>
              </p>
              <p className="font-medium text-brand-black">{laptop.cpu || '—'}</p>
            </div>
            <div className="p-2.5 bg-white rounded border border-brand-border">
              <p className="font-semibold text-brand-muted flex items-center space-x-1">
                <HardDrive className="w-3.5 h-3.5 text-brand-green" />
                <span>Memory & Storage</span>
              </p>
              <p className="font-medium text-brand-black">
                {laptop.ram || '—'} {laptop.storage ? `/ ${laptop.storage}` : ''}
              </p>
            </div>
            <div className="p-2.5 bg-white rounded border border-brand-border">
              <p className="font-semibold text-brand-muted flex items-center space-x-1">
                <Battery className="w-3.5 h-3.5 text-brand-green" />
                <span>Battery & Charger</span>
              </p>
              <p className="font-medium text-brand-black">
                {laptop.battery_health != null ? `${laptop.battery_health}% Health` : 'N/A'} •{' '}
                {laptop.charger_included ? 'Charger incl.' : 'No charger'}
              </p>
            </div>
            <div className="p-2.5 bg-white rounded border border-brand-border">
              <p className="font-semibold text-brand-muted">Display & Touch</p>
              <p className="font-medium text-brand-black">
                {laptop.screen_size || 'Standard Screen'}
                {laptop.touchscreen === true ? ' • Touchscreen' : laptop.touchscreen === false ? ' • Non-Touch' : ''}
              </p>
            </div>
            <div className="p-2.5 bg-white rounded border border-brand-border">
              <p className="font-semibold text-brand-muted">Discrete GPU</p>
              <p className="font-medium text-brand-black">{laptop.gpu || '—'}</p>
            </div>
          </div>

          {/* Supplier (Owner/Manager) */}
          {canViewCosts && laptop.supplier && (
            <div className="flex items-center space-x-2 text-xs text-brand-muted bg-white p-2.5 rounded border border-brand-border">
              <Building2 className="w-4 h-4 text-brand-green" />
              <span>Supplier: <strong className="text-brand-black">{laptop.supplier.name}</strong></span>
            </div>
          )}

          {/* Notes */}
          {laptop.notes && (
            <div className="p-3 bg-amber-50/50 rounded border border-amber-200/60 text-xs">
              <p className="font-semibold text-amber-900 mb-0.5">Notes</p>
              <p className="text-amber-800 break-words">{laptop.notes}</p>
            </div>
          )}

          {/* Status History Timeline */}
          {history.length > 0 && (
            <div className="pt-2 border-t border-brand-border">
              <h4 className="text-xs font-bold text-brand-black uppercase tracking-wider mb-2 flex items-center space-x-1">
                <Clock className="w-3.5 h-3.5 text-brand-green" />
                <span>Status History</span>
              </h4>
              <div className="space-y-2">
                {history.map((h) => (
                  <div key={h.id} className="flex items-center justify-between text-xs p-2 bg-brand-bg rounded border border-brand-border">
                    <div>
                      <span className="font-semibold text-brand-black">{h.to_status}</span>
                      {h.from_status && <span className="text-brand-muted"> (from {h.from_status})</span>}
                      {h.reason && <p className="text-[11px] text-brand-muted">{h.reason}</p>}
                    </div>
                    <span className="text-[10px] text-brand-muted shrink-0">{formatDate(h.changed_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* QR Stock Tag Label Modal */}
      {showQrModal && laptop && (
        <Modal isOpen title={`Stock Label — ${laptop.stock_id}`} onClose={() => setShowQrModal(false)}>
          <div className="flex flex-col items-center p-4">
            <div className="w-[85mm] h-[54mm] max-w-full bg-white border-2 border-brand-black rounded-lg p-3 flex flex-col justify-between shadow-md">
              <div className="flex justify-between items-start border-b border-brand-border pb-1">
                <div>
                  <h4 className="font-bold text-xs text-brand-black tracking-tight">AL KUWAIT TECH</h4>
                  <p className="text-[9px] text-brand-muted">Stock ID: {laptop.stock_id}</p>
                </div>
                <span className="font-bold text-xs text-brand-green">Rs. {(laptop.asking_price ?? 0).toLocaleString('en-PK')}</span>
              </div>
              <div className="my-1">
                <p className="font-bold text-xs text-brand-black">{laptop.brand} {laptop.model}</p>
                <p className="text-[10px] text-brand-muted truncate">
                  {laptop.cpu || ''} {laptop.ram ? `• ${laptop.ram}` : ''} {laptop.storage ? `• ${laptop.storage}` : ''}
                </p>
                <p className="text-[9px] text-brand-muted mt-0.5">S/N: {laptop.serial_number || 'N/A'}</p>
              </div>
              <div className="text-[8px] text-brand-muted pt-1 border-t border-brand-border text-center">
                Scan barcode/QR for verification
              </div>
            </div>
            <button
              onClick={() => window.print()}
              className="mt-4 px-4 py-2 bg-brand-green text-white text-xs font-semibold rounded-md hover:bg-brand-green-hover transition-colors"
            >
              Print Label (85x54mm)
            </button>
          </div>
        </Modal>
      )}

      {/* Footer */}
      <div className="flex justify-end pt-3 border-t border-brand-border">
        <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black transition-colors">
          Close
        </button>
      </div>
    </Modal>
  );
};
